import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from '@travelity/form';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { ProductType } from '@travelity/api';
import { useTranslation } from 'react-i18next';
import { ProductDetailsForm } from '../../../components/product-details';
import schema from '../../../components/product-details/product-details.schema';
import StepContent from './step-content';
import { useStepperContext } from '../../../components/stepper';
import { AddProductFormState } from '../add-product.types';
import { useHelperContext } from '../../../contexts/helper-context';

export interface ProductDetailsStepProps {}

const ProductDetailsStep: React.FC<ProductDetailsStepProps> = () => {
    const { t } = useTranslation();
    const { state, next, showErrors } =
        useStepperContext<AddProductFormState>();
    const { productType } = useParams() as { productType: ProductType };
    const onSubmit = useCallback((data: Record<string, any>) => {
        // we can have event as a second parameter if we want to handle back as well
        next({ ...state, productInfo: data });
    }, []);
    const { Form, formState, watch } = useForm({
        onSubmit,
        schema,
        context: { type: productType },
        mode: 'onChange',
        validateInitially: true,
        defaultValues: state.productInfo,
    });
    const errors = useMemo(() => {
        return Object.values(formState.errors)
            .map(error => error?.message)
            .filter(v => v) as string[];
    }, [formState]);

    // Handle helper texts
    const { setInfo } = useHelperContext();
    useEffect(() => {
        setInfo();
    }, [setInfo]);

    useEffect(() => {
        const callback = () => {
            setInfo(
                <>
                    <Box sx={{ fontWeight: 'bold' }}>{t('Product Name')}</Box>
                    Product name is a max 50 characters long identifier, that
                    should be unique across the product names you use.
                    <br />
                    You can type any name you like or select the auto-generated
                    name provided by the Travelity
                </>
            );
        };
        const el = document.querySelector('input[name=name]');
        el?.addEventListener('focus', callback);
        return () => el?.removeEventListener('focus', callback);
    }, []);

    const languages = watch('languages');
    const productName = watch('name');
    useEffect(() => {
        if (languages && productName) {
            setInfo(
                <>
                    Product tags will help travelers across various platforms to
                    faster understand what your product is about
                </>
            );
        }
    }, [languages, productName]);

    return (
        <StepContent title="Product Details" errors={errors}>
            <Form id="step">
                <ProductDetailsForm
                    isNew
                    type={productType}
                    showErrors={showErrors}
                />
            </Form>
        </StepContent>
    );
};

export default React.memo(ProductDetailsStep);
